import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {
    DetectMethodChanges,
    DxValueChanged,
    findHCF,
    getActionClass,
    getBucketRoleClass,
    getQtyClass, isValidNumber,
    isVoid
} from "../utils";
import {RegroupOrdersConfig} from "./RegroupOrdersConfig";
import {DropZone} from "./DropZone";
import {OrdersGroupToRegroup} from "./OrdersGroupToRegroup";
import {ToastrService} from "ngx-toastr";
import {SolutionOrderLegDto} from "../adjustment-pricing-grid/model/SolutionOrderLegDto";
import {DropZoneOrder, OrderToRegroupWrapper} from "./OrderToRegroupWrapper";
import {SolutionPositionDto} from "../adjustment-pricing-grid/model/SolutionPositionDto";
import {RegroupedOrders} from "./RegroupedOrders";
import * as Enumerable from "linq";
import {ApgDataService} from "../adjustment-pricing-grid/services/apg-data.service";
import {HedgePositionsService} from "../hedging-grid/positions-section/hedge-positions/hedge-positions.service";
import {GenericConfirmationDialogComponent} from "../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {MessageBusService} from "../message-bus.service";
import {LastQuoteCacheService} from "../last-quote-cache.service";
import {TradingInstrumentDisplayNameService} from "../trading-instruments/trading-instrument-display-name.service";
import {
    ArrangementPlan,
    ArrangementPlanPages,
    arrangementRequestKey,
    planOptimalArrangement
} from "./arrangement-planner";
import {OoaClientService} from "./ooa-client.service";
import {sortBoxes, sortLegs, zoneTitleForIndex} from "./leg-order";

const ARRANGEMENT_TITLE = 'Re-Arrange';

// `zoneTitleForIndex` moved to leg-order.ts (CR-20260827-001) so the verification dialog's
// read-only preview names its orders by the same position-pinned rule as `reorderDropZones()`.

@Component({
    selector: 'ets-orders-regroup-dialog',
    templateUrl: 'orders-regroup-dialog.component.html',
    styleUrls: ['./orders-regroup-dialog.component.scss']
})

export class OrdersRegroupDialogComponent implements OnInit {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _toastr: ToastrService,
        private readonly _apgDataService: ApgDataService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _ooaClient: OoaClientService,
    ) {
    }

    private _priceUpdateInterval: number;

    private _config: RegroupOrdersConfig;

    get width(): number {
        return isVoid(this.orderGroups2) ? 880 : 1340;
    }

    get showSecondSide(): boolean {
        return !isVoid(this.orderGroups2);
    }

    get context() {
        return this._config?.context;
    }

    get canCopyAllOrders(): boolean {
        return this.dropZones.some(dz => dz.items.length > 0);
    }

    portfolioDefaultQty: number;

    visible = false;

    loading = false;

    /** Blocks the dialog (shaded load panel) while the backend arrangement is in flight. */
    arrangementLoading = false;

    splitQty = false;

    private _settingSplitQtyInternally = false;

    get orderGroups(): OrdersGroupToRegroup[] {
        return this._config?.orders;
    }

    get orderGroups2(): OrdersGroupToRegroup[] {
        return this._config?.orders2;
    }

    readonly dropZones: DropZone[] = [];

    readonly dropZones2: DropZone[] = [];

    /** The backend's ranked answer for the CURRENT screen; pages are what the pager slides over. */
    arrangementPages: ArrangementPlanPages = null;

    arrangementPageIx = 0;

    /** True when the applied page carries naked residue — RED replaces orange (owner ruling). */
    appliedDegraded = false;

    get arrangementPageCount(): number {
        return this.arrangementPages && this.arrangementPages.status === 'ok'
            ? this.arrangementPages.pages.length
            : 0;
    }

    private get atm(): number | null {
        return isValidNumber(this._config?.atm) ? this._config.atm : null;
    }

    @ViewChild(GenericConfirmationDialogComponent) confirmationDialog: GenericConfirmationDialogComponent;

    ngOnInit() {
    }

    @DetectMethodChanges({isAsync: true})
    async show(config: RegroupOrdersConfig) {
        this._config = config;

        // The source side is sorted ONCE, here. Its groups are rebuilt by the caller on every
        // open (all three callers - APG, Hedging Grid, ML Pad - construct fresh arrays), so
        // sorting them in place changes nothing that outlives this dialog.
        this.sortSourceGroups(config.orders);
        this.sortSourceGroups(config.orders2);

        this.dropZones.push(this.makeDropZone(0));

        if (isValidNumber(this._config.defaultQty, true)) {
            this.portfolioDefaultQty = this._config.defaultQty;
        } else {
            const defaultQtyForPortfolio =  await this._apgDataService
                .getDefaultQtyForPortfolio(config.portfolio);
            this.portfolioDefaultQty = defaultQtyForPortfolio;
        }

        if (this.context === 'mlpad') {
            this._priceUpdateInterval = setInterval(() => {
               this._config.orders.forEach(orders => {
                   orders.orders.forEach(order => {
                       const mid = this._lastQuoteCache.getLastQuote(order.ticker)?.mid;
                       if (isValidNumber(mid)) {
                           order.order.price = mid;
                       }
                   });
               });

               if (!isVoid(this._config.orders2)) {
                   this._config.orders2.forEach(orders => {
                       orders.orders.forEach(order => {
                           const mid = this._lastQuoteCache.getLastQuote(order.ticker)?.mid;
                           if (isValidNumber(mid)) {
                               order.order.price = mid;
                           }
                       });
                   });
               }

               this._changeDetector.detectChanges();
            }, 1000) as any;
        }

        this.visible = true;

        // THE ARRANGEMENT RUNS EVERY TIME the caller asks for it (`autoArrangeOnOpen`; owner
        // ruling 2026-08-30 for the Hedging Grid, replacing CR-20260827-001's improvement-gated
        // autoArrange). The dialog opens, the backend calculation fires behind the blocking load
        // panel, and the best solution is applied as the starting point - the pager slides to
        // the alternatives. 'Unchanged' opens the dialog exactly as the old no-improvement path
        // did; a failure opens it un-optimized with a warning.
        // Without the flag (APG, ML Pad - owner ruling 2026-09-18) the dialog opens CLEAN: no
        // request is sent, the user arranges by hand or presses Re-Arrange.
        if (this._config.autoArrangeOnOpen === true) {
            await this.fetchAndApplyArrangement();
        }
    }

    /**
     * Fetch the backend arrangement and apply the best page. The zones are empty at this moment,
     * so the "rebuilds every order from scratch" confirmation cannot trigger.
     *
     * ⚠ ALWAYS PLANNED AGAINST THE DIALOG'S OWN ROWS (CR-20260830-003). This used to accept the
     * transaction popup's pre-composed pages when their requestKey matched - but a plan's legs
     * reference the WRAPPER INSTANCES it was planned over, and the popup builds its own wrapper
     * set: applying its pages settled those invisible wrappers while the rows on screen stayed
     * fully available (white, draggable, open to double-booking). The key compares contract data,
     * so it can never catch that. Re-planning here is what binds the legs to the visible rows,
     * and it costs no round trip: the popup's probe already warmed `OoaClientService`'s
     * canonical-key cache with exactly this request.
     */
    private async fetchAndApplyArrangement(): Promise<void> {

        this.arrangementLoading = true;

        try {
            const pages = await planOptimalArrangement(
                this.orderGroups, this.orderGroups2, this._ooaClient, this.atm);

            this.arrangementPages = pages;
            this.arrangementPageIx = 0;

            if (pages.status === 'ok' && pages.pages.length > 0) {
                console.log(`[arrange] requestId=${pages.requestId || 'cached'} applied page 1/${pages.pages.length}`);
                this.applySolutionPage(0);
                await this.relabelLegs();
            } else if (pages.status === 'error') {
                pages.warnings.forEach(warning =>
                    this._toastr.warning(warning, ARRANGEMENT_TITLE, {timeOut: 20000, extendedTimeOut: 5000}));
            }
            // 'unchanged': the screen grouping is already the engine's best answer - open as-is.
        } catch (e) {
            console.error('[arrange] auto-arrangement failed - dialog opens un-optimized', e);
        } finally {
            this.arrangementLoading = false;
        }
    }

    /**
     * Sort one side's source groups: the legs inside each group, then the groups themselves by the
     * highest strike each one holds.
     *
     * A group's title is its own data here ('c.sling', 'Main', a hedge label), so it travels with
     * the group and needs no re-designation - unlike a drop zone, whose name is its position.
     */
    private sortSourceGroups(groups: OrdersGroupToRegroup[]) {

        if (isVoid(groups)) {
            return;
        }

        groups.forEach(group => sortLegs(group.orders));

        sortBoxes(groups, group => group.orders);
    }

    /**
     * Put the right-hand side back into its required order and re-designate the zone names to
     * match the new positions.
     *
     * CALL THIS AFTER ANYTHING THAT MOVES A LEG OR A ZONE. The sort is an invariant of
     * `zone.items`, not a view applied at render time, because that array is what
     * `copyAllOrders()` hands to the copy callback - a display-only sort would send the ticket out
     * in a different order from the one on screen.
     *
     * The re-designation is the other half of the same requirement. `zone.title` is what the
     * legend draws AND what travels to the callback as `orderToCopy` - one field, so the two can
     * never disagree; re-issuing it here is what keeps that one field honest once zones can move.
     * (The legend used to derive its own name from the zone's index instead. That agreed with the
     * title only for as long as zones were appended and never re-ordered.)
     */
    private reorderDropZones() {

        this.dropZones.forEach(zone => sortLegs(zone.items));

        sortBoxes(this.dropZones, zone => zone.items);

        this.dropZones.forEach((zone, ix) => zone.title = zoneTitleForIndex(ix));
    }

    @DetectMethodChanges()
    onHidden() {
        clearInterval(this._priceUpdateInterval);
        this.dropZones.length = 0;
        this.splitQty = false;
        this.visible = false;
        this.arrangementPages = null;
        this.arrangementPageIx = 0;
        this.appliedDegraded = false;
        this.loading = false;
        this.arrangementLoading = false;
    }

    onDragStart($event: DragEvent, order: OrderToRegroupWrapper) {
        $event.dataTransfer.setData("text", `${order.ticker},${order.group.title}`);
    }

    @DetectMethodChanges()
    onDrop($event: DragEvent, zone: DropZone) {

        $event.preventDefault();

        if (isVoid(zone)) {
            return;
        }

        this.loading = true;

        const id = $event.dataTransfer.getData("text");

        const parts = id.split(',');

        if (parts.length !== 2) {
            this._toastr.error('Order Not Recognized');
            return;
        }

        const ticker = parts[0];
        const title = parts[1];

        const self = this;

        setTimeout(() => {
            try {

                if (!ticker || !title) {
                    return;
                }

                if (zone.items.length >= 4) {
                    this._toastr.error('Order cannot have more than 4 legs');
                    return;
                }

                const side = ticker.indexOf(' Call ') >= 0 ? 'Call' : 'Put';

                const orderGroups = isVoid(this.orderGroups2)
                    ? this.orderGroups
                    : (side === 'Call' ? this.orderGroups : this.orderGroups2);

                const item = orderGroups
                    .find(x => x.title === title)
                    ?.orders.find(x => x.ticker === ticker);

                if (isVoid(item)) {
                    this._toastr.error('Drop operation cannot be completed', 'Error');
                    return;
                }


                const index = zone.items.findIndex(x => x.ticker === ticker);

                if (index >= 0) {
                    this._toastr.error('Selected leg already added to this order', 'Error');
                    return;
                }

                const copy = new DropZoneOrder(item);
                copy.group = item.group;

                zone.add(copy);

                (item as any).moved = true;

                // A hand-dropped leg lands in its sorted place, not at the bottom of the box - and
                // if it is the new highest strike, its box takes the top slot and the "Main" name
                // with it (owner ruling, 2026-08-24).
                this.reorderDropZones();
            } finally {
                self.relabelLegs()
                    .then(() => {
                        this.loading = false;
                        this._changeDetector.detectChanges();
                    });
            }
        });
    }

    onDragOver($event: DragEvent) {
        $event.preventDefault();
    }

    @DetectMethodChanges()
    addDropZone() {
        if (this.dropZones.length > 4) {
            this._toastr.error('Cannot have more than 4 orders');
            return;
        }
        this.dropZones.push(this.makeDropZone(this.dropZones.length));

        // The new zone is empty, so the sort leaves it at the bottom; the call is here so its name
        // is settled by the same rule as every other zone's rather than by where it was appended.
        this.reorderDropZones();
    }


    @DetectMethodChanges()
    removeOrder(order: DropZoneOrder) {
        this.removeOrderSilent(order);

        // Pulling a leg out can lower the box's highest strike, which can re-rank the boxes.
        this.reorderDropZones();
    }

    private removeOrderSilent(order: DropZoneOrder) {
        this.dropZones.forEach(dz => {
            const ix = dz.items.indexOf(order);
            if (ix < 0) {
                return;
            }
            dz.items.splice(ix, 1);
        });

        order.onRemoved();
    }

    getActionClass(order: SolutionOrderLegDto, isEvenRow: boolean) {
        const classes = [];

        const actionClass = getActionClass(order);

        classes.push(actionClass);

        if (isEvenRow) {
            classes.push('couple-start');
        } else {
            classes.push('couple-end');
        }

        return classes;
    }

    getPositionRoleClass(pos: SolutionPositionDto | SolutionOrderLegDto): string {
        return getBucketRoleClass(pos);
    }

    getUnderlyingDisplayName(order: OrderToRegroupWrapper): string {
        return this._displayNameService.getDisplayNameForTicker(order.underlying);
    }

    //
    getQtyClass(position: any): any {
        return getQtyClass(position);
    }

    @DetectMethodChanges()
    removeZone(zone: DropZone) {

        const slice = zone.items.slice();

        slice.forEach(item => {
            this.removeOrderSilent(item);
        });

        const ix = this.dropZones.indexOf(zone);

        if (ix < 0) return;

        this.dropZones.splice(ix, 1);

        // Removing a zone shifts every zone below it up one, so the names have to be re-issued.
        this.reorderDropZones();
    }

    async copySingleOrder(zone: DropZone) {

        if (this.context === 'mlpad') {
            this.copySingleOrderToDestination('ToS', zone);
            return;
        }

        const error = this.validatePriceSides(zone);

        if (error) {
            this._toastr.error(error);
            return;
        }

        let orders = zone.items;

        try {
            await this._config.reverseOrderValidator(orders as any);
        } catch {
            return;
        }

        const cbArg = {
            orderToCopy: zone.title,
            orders,
            limitPrice: zone.limitPrice
        } as RegroupedOrders;

        this._config.copyCallback([cbArg]);
    }

    /**
     * Validates if limit prices, provided by user match the trade side. That is: debit trade must have debit (negative) limit px,
     * credit trade - credit (positive) limit px.
     *
     * @returns Error message if validation fails, or nothing is validation passes
     *
     * @private
     */
    private validatePriceSides(zone: DropZone) {

        if (!isValidNumber(zone.limitPrice, true)) {
            return;
        }

        const chunkMarketPrice = this.getChunkMarketPrice(zone, true);

        const sign = Math.sign(chunkMarketPrice);

        const limitSign = Math.sign(zone.limitPrice);

        if (sign === limitSign) {
            return;
        }

        const msg = `The Limit Price and Trade Side Don't Match For this Order: ${zone.title}. 
            Make Sure Debit Trades Have Debit Limit Price and Credit Trades - Credit Limit Price`;

        return msg;
    }

    async copyAllOrders() {

        if (this.context === 'mlpad') {
            this.copyAllOrdersToDestination('ToS');
            return;
        }


        const error = this.validate();

        if (!isVoid(error)) {
            this._toastr.error(error);
            return;
        }

        const allOrders = this.dropZones.map(dz => {

            // const orders = dz.items.map(y => y.order);
            const orders = dz.items;

            return {
                orderToCopy: dz.title,
                orders,
                limitPrice: dz.limitPrice
            } as RegroupedOrders;
        });

        const flatOrders = allOrders.flatMap(x => x.orders);

        try {
            await this._config.reverseOrderValidator(flatOrders as any);
        } catch {
            return;
        }

        this._config.copyCallback(allOrders);
    }

    /**
     * RE-ARRANGE. One click turns a pile of legs into well-formed execution tickets. Named by
     * the owner 2026-08-21; since CR-20260830-002 the decomposition itself runs on the BACKEND
     * (a port of the owner's reference script) and this button applies its ranked answer -
     * usually a cache hit, since the same request already ran when the dialog opened.
     */
    @DetectMethodChanges({isAsync: true})
    async runOptimalOrderArrangement() {

        if (this.dropZones.some(dz => dz.items.length > 0)) {
            try {
                await this.confirmationDialog.show([
                    'Re-Arrange rebuilds every order from scratch.',
                    'The orders you have already arranged will be discarded.',
                    'Continue?'
                ]);
            } catch {
                return;
            }
        }

        this.arrangementLoading = true;

        try {

            let pages = this.arrangementPages;

            const key = arrangementRequestKey(this.orderGroups, this.orderGroups2, this.atm);

            if (isVoid(pages) || pages.requestKey !== key) {
                pages = await planOptimalArrangement(
                    this.orderGroups, this.orderGroups2, this._ooaClient, this.atm);
                this.arrangementPages = pages;
                this.arrangementPageIx = 0;
            }

            if (pages.status === 'ok' && pages.pages.length > 0) {
                const pageIx = Math.min(this.arrangementPageIx, pages.pages.length - 1);
                console.log(`[arrange] requestId=${pages.requestId || 'cached'} applied page ` +
                    `${pageIx + 1}/${pages.pages.length}`);
                this.applySolutionPage(pageIx);
                await this.relabelLegs();
            } else {
                // The user confirmed a rebuild-from-scratch, so the clean empty state is the
                // truthful one whether the engine says "already best" or failed.
                this.clearToEmpty();

                if (pages.status === 'unchanged') {
                    this._toastr.warning('These orders are already the best arrangement for ' +
                        'execution - nothing was changed.', ARRANGEMENT_TITLE);
                }

                pages.warnings.forEach(warning =>
                    this._toastr.warning(warning, ARRANGEMENT_TITLE, {timeOut: 20000, extendedTimeOut: 5000}));
            }

        } catch (e) {

            console.error(e);

            // By this point the orders have already been cleared and every source row reset, so
            // "nothing has been changed" would be a lie. Leave the dialog in the clean empty state
            // it is actually in, and say so.
            this.clearToEmpty();

            this._toastr.error('Could not arrange these orders. The orders have been cleared - ' +
                'arrange them by hand, or try again.', ARRANGEMENT_TITLE);

        } finally {
            this.arrangementLoading = false;
        }
    }

    /**
     * Slide the pager: re-apply the k-th ranked solution. A full reset-and-rebuild with the same
     * semantics as Re-Arrange - hand edits made after an apply are discarded, deliberately
     * without a confirmation (flagged on the smoke charter for owner sign-off).
     */
    @DetectMethodChanges({isAsync: true})
    async onArrangementPageChanged(pageIx: number) {

        if (this.arrangementPageCount === 0 || pageIx < 0 || pageIx >= this.arrangementPageCount) {
            return;
        }

        console.log(`[arrange] requestId=${this.arrangementPages.requestId || 'cached'} applied page ` +
            `${pageIx + 1}/${this.arrangementPages.pages.length}`);

        this.applySolutionPage(pageIx);

        await this.relabelLegs();
    }

    /**
     * THE SYNCHRONOUS APPLY BLOCK. Clears the zones, resets every source row, and places one
     * plan page. ⚠ `_settingSplitQtyInternally` is raised ONLY around this synchronous body and
     * never across a network await - the 2026-08-21 arrangement-deletion bug lived exactly in
     * that widened window (see `onSplitQtyModeChanged`).
     *
     * Split-Qty mode is switched on first and unconditionally: splitting quantities is not an
     * option the arrangement might take, it is how the arrangement works.
     *
     * EVERY SOURCE ROW IS RESET, not just the ones sitting in an order. `removeZone` reverses
     * only what it placed, but netting settles rows that cancel each other out without ever
     * putting them in an order - so a re-apply would otherwise start from a poisoned residue.
     */
    private applySolutionPage(pageIx: number): void {

        const page: ArrangementPlan = this.arrangementPages.pages[pageIx];

        this._settingSplitQtyInternally = true;

        try {

            this.dropZones.slice().forEach(dz => this.removeZone(dz));

            this.getAllSourceOrders().forEach(order => {
                order.settledQty = 0;
                (order as any).moved = false;
            });

            this.splitQty = true;

            if (page.tickets.length === 0) {
                this.dropZones.push(this.makeDropZone(0));
            } else {

                // Rows that net each other out are accounted for without being traded.
                page.cancelled.forEach(leg => leg.wrapper.adjustSettledQty(leg.qty));

                // NO ZONE CAP HERE (owner: "we produce that many tickets") - the plan renders as
                // many zones as it holds; the manual Add Linked Order cap is untouched.
                page.tickets.forEach((ticket, ix) => {
                    const zone = this.makeDropZone(ix);
                    zone.naked = ticket.shape === 'naked';
                    ticket.legs.forEach(leg => zone.add(new DropZoneOrder(leg.wrapper, leg.qty)));
                    this.dropZones.push(zone);
                });

                // The plan holds legs and tickets in the order the decomposition produced them,
                // which is not the order they are read and traded in. Ranking happens here, after
                // the plan is placed - the decomposition itself is left alone.
                this.reorderDropZones();
            }

            this.arrangementPageIx = pageIx;
            this.appliedDegraded = page.degraded === true;

            // One toast per warning. A naked pile raises exactly one (the planner's "ship as a
            // naked order" line - owner 2026-09-09); the naked zone itself carries the red icon
            // and the "execute this order before the others" line, so no second toast here.
            page.warnings.forEach(warning =>
                this._toastr.warning(warning, ARRANGEMENT_TITLE, {timeOut: 20000, extendedTimeOut: 5000}));

        } finally {
            this._settingSplitQtyInternally = false;
        }
    }

    /** The clean empty state: zones cleared, every source row reset, one empty "Main". */
    private clearToEmpty(): void {
        this.dropZones.slice().forEach(dz => this.removeZone(dz));
        this.getAllSourceOrders().forEach(order => {
            order.settledQty = 0;
            (order as any).moved = false;
        });
        this.dropZones.push(this.makeDropZone(0));
        this.appliedDegraded = false;
    }

    private makeDropZone(index: number): DropZone {
        const zone = new DropZone();
        zone.title = zoneTitleForIndex(index);
        return zone;
    }

    private getAllSourceOrders(): OrderToRegroupWrapper[] {
        const first = (this.orderGroups || []).reduce(
            (acc, g) => acc.concat(g.orders || []), [] as OrderToRegroupWrapper[]);
        const second = (this.orderGroups2 || []).reduce(
            (acc, g) => acc.concat(g.orders || []), [] as OrderToRegroupWrapper[]);
        return first.concat(second);
    }

    @DetectMethodChanges()
    onSplitQtyModeChanged($event: any) {

        // TICKING THIS BOX CLEARS EVERY ORDER, WHICH IS RIGHT WHEN THE USER TICKS IT AND FATAL
        // WHEN THE CODE DOES. "Re-Arrange" switches Split-Qty on as its first act, and dxCheckBox
        // raises onValueChanged for a programmatic write exactly as it does for a click.
        //
        // ⚠ A FLAG ALONE WAS NOT ENOUGH - IT SHIPPED, AND IT DELETED THE ARRANGEMENT (2026-08-21).
        // `runOptimalOrderArrangement` is wrapped in @DetectMethodChanges({isAsync: true}), whose
        // change detection runs in the decorator's .finally() - AFTER the method's own finally has
        // already lowered the flag. So the checkbox saw false->true with the guard already down,
        // this handler ran, and `removeZone` deleted every order it had just built and un-settled
        // every source row. On screen: one empty "Main", Split-Qty on, every leg back to n/n, and
        // no error anywhere.
        //
        // THE LOAD-BEARING GUARD IS `$event.event`, not the flag. Verified in DevExtreme 21.2.3:
        // `renovation/ui/editors/check_box.js` `onWidgetClick(event)` calls `saveValueChangeEvent(event)`
        // for pointer AND keyboard, and `ui/editor/editor.js` clears it with
        // `_saveValueChangeEvent(void 0)` right after raising the action - so a model write always
        // arrives here with no source event. The flag stays only as a second line of defence; do
        // not make it the only one again.
        if (isVoid($event?.event) || this._settingSplitQtyInternally) {
            return;
        }

        if (this.dropZones.length === 0) {
            return;
        }

        this.dropZones
            .slice()
            .forEach(dz => this.removeZone(dz));

        // One empty zone is left behind, so it is the first one, so it is "Main". Reading the old
        // first zone's title and carrying it over used to say the same thing; now that a zone's
        // name is decided by its position, saying it once is enough.
        this.dropZones.push(this.makeDropZone(0));
    }

    @DetectMethodChanges({isAsync: true})
    async onSettledQtyChanged(value: number, order: DropZoneOrder) {

        const orderSideMultiplier = Math.sign(order.order.qty);

        if (Math.sign(value) !== orderSideMultiplier) {
            this._toastr.error("The quantity you entered doesn't match order's action");
            return;
        }

        const prev = Math.abs(order.qty || 0) * orderSideMultiplier;

        const curr = Math.abs(value || 0) * orderSideMultiplier;

        const delta = curr - prev;

        const adjusted = order.adjustSettledQty(delta);

        if (!adjusted) {
            this._toastr
                .error('Cannot adjust order qty. Please, check that total settled quantity matches original order\'s quantity');
        } else {
            order.qty = value;
        }

        await this.relabelLegs()
    }

    private validate(): string {

        // Rule 1: one order should not contain 2 same tickers
        for (const zone of this.dropZones) {
            const duplicates = Enumerable.from(zone.items)
                .groupBy(x => x.ticker)
                .count(x => x.count() > 1);

            if (duplicates > 0) {
                return `Same contract legs are not allowed within one order (${zone.title})`;
            }
        }


        // Rule 2: split orders qty must match
        if (this.splitQty) {
            const summarized = Enumerable.from(this.dropZones)
                .selectMany(x => x.items)
                .groupBy(x => x.ticker)
                .select(x => {
                    return {
                        ticker: x.key(),
                        qty: x.sum(y => y.qty)
                    }
                }).toArray();

            // SUM the source rows for a contract, do not take the FIRST one. Pooling makes two
            // groups holding the same contract routine: ship the netted 30 and a `find` would
            // compare it against one group's 10 and refuse a correct arrangement. With a single
            // row per contract - every hand-dragged case - the sum is that row, unchanged.
            const originals = this.orderGroups.flatMap(x => x.orders);
            for (const summarizedElement of summarized) {
                const matches = originals.filter(x => x.ticker === summarizedElement.ticker);
                if (isVoid(matches)) {
                    continue;
                }
                const originalQty = matches.reduce((acc, x) => acc + x.qty, 0);
                if (originalQty !== summarizedElement.qty) {
                    return "Regrouped orders' quantity does not match original orders' quantity;";
                }
            }
        }

        // side of prices should match
        for (const zone of this.dropZones) {

            const error = this.validatePriceSides(zone);

            if (!isVoid(error)) {
                return error;
            }
        }
    }

    getPriceInputStyle(px: number): string {
        let style = 'text-align: center;';
        if (px > 0) {
            style += 'color: green';
        } else if (px < 0) {
            style += 'color: red';
        }
        return style;
    }

    async relabelLegs() {


        try {


            let positions = [];

            if (this._config.context === 'hg') {
                const hgPositions = await this._hedgePositionsService.getHedgePositions(this._config.portfolio);
                positions = hgPositions.map(x => {
                    return {
                        ticker: x.ticker,
                        hedgeName: x.label,
                        qty: x.qty
                    }
                });
            } else if (this._config.context === 'apg') {
                const apgPositions = await this._apgDataService.getPortfolioPositions(this._config.portfolio);
                positions = apgPositions.flatMap(x => x)
                    .map(x => {
                        return {
                            ticker: x.ticker,
                            hedgeName: x.strategy,
                            qty: x.qty
                        }
                    });
            } else {
                this.dropZones.forEach(dz => dz.items.forEach(x => x.ownAction = x.action));
                return;
            }


            const placedLegs = this.dropZones.flatMap(x => x.items);

            // A HAND-DRAGGED LEG STILL BELONGS TO ITS HEDGE; AN ARRANGED ONE DOES NOT. "Optimal
            // Order Arrangement" pools every group into one execution package, so its legs carry
            // no hedge name and the per-hedge match below can never hit - which would silently
            // label every arranged leg "To Open", including legs that close a position. For a
            // pooled arrangement the question becomes "what do I hold in this contract, in
            // total?", so the per-hedge rows are summed first and matched on contract alone.
            const isPooled = placedLegs.some(leg => isVoid(leg.group));

            if (isPooled) {
                const byTicker: { ticker: string, hedgeName: string, qty: number }[] = [];
                positions.forEach(position => {
                    const seen = byTicker.find(x => x.ticker === position.ticker);
                    if (seen) {
                        seen.qty += position.qty;
                    } else {
                        byTicker.push({ticker: position.ticker, hedgeName: undefined, qty: position.qty});
                    }
                });
                positions = byTicker;
            }

            const matchOnContractAlone = isPooled || this._config.context === 'apg';

            placedLegs
                .forEach((item) => {

                    const predicate = matchOnContractAlone
                        ? (x) => x.ticker === item.ticker
                        : (x) => x.ticker === item.ticker && x.hedgeName === item.group?.title

                    let existing: { ticker: string, qty: number } = positions
                        .find(predicate);

                    let action = item.action;

                    if (isVoid(existing)) {

                        const newPos = {ticker: item.ticker, hedgeName: item.group?.title, qty: item.qty};
                        positions.push(newPos);
                        action = item.qty > 0 ? 'Buy' : 'Sell';
                        action = `${action} To Open`;
                        item.ownAction = action;

                    } else {
                        const beforeQty = existing.qty;
                        const newQty = beforeQty + item.qty;

                        if (newQty === 0) {
                            if (beforeQty > 0) {
                                action = 'Sell To Close';
                            } else if (beforeQty < 0) {
                                action = 'Buy To Close';
                            }
                        } else {
                            if (Math.sign(beforeQty) === 0) {
                                if (newQty > 0) {
                                    action = 'Buy To Open';
                                } else if (newQty < 0) {
                                    action = 'Sell To Open';
                                }
                            } else {
                                if (Math.sign(newQty) === Math.sign(beforeQty)) {
                                    if (newQty > 0) {
                                        if (newQty > beforeQty) {
                                            action = 'Buy More';
                                        } else if (newQty < beforeQty) {
                                            action = 'Sell To Reduce';
                                        }
                                    } else if (newQty < 0) {
                                        if (newQty < beforeQty) {
                                            action = 'Sell More';
                                        } else if (newQty > beforeQty) {
                                            action = 'Buy To Reduce';
                                        }
                                    }
                                } else {
                                    if (newQty > 0) {
                                        action = 'Buy To Reverse';
                                    } else if (newQty < 0) {
                                        action = 'Sell To Reverse';
                                    }


                                }
                            }
                        }

                        item.ownAction = action;
                        existing.qty = newQty;
                    }
                });

        } catch (e) {
            console.error(e);
        }
    }

    dropZoneHasLimitPrice(zone: DropZone) {
        return isValidNumber(zone.limitPrice, true);
    }

    @DetectMethodChanges()
    onLimitPriceChanged(args: DxValueChanged<number>, zone: DropZone) {
    }


    // Original Prices
    getOriginalMarketPrice(slot: 'first' | 'second' = 'first'): number {

        const hcf = this.getOriginalHcf(slot);

        const orders = (isVoid(slot) || slot == 'first')
            ? this._config.orders
            : this._config.orders2;

        const prices = orders.map(og => {
            const prices = og.orders
                .map(y => Math.abs(y.price) * y.qty * -1);

            const sum = prices
                .reduce((p, c) => p + c, 0);
            return sum;
        });

        const total = prices.reduce((p, c) => p + c);

        const adjusted = total / hcf;

        return adjusted;
    }

    getOriginalLedgerPrice(slot: 'first' | 'second' = 'first'): number {
        const marketPrice = this.getOriginalMarketPrice(slot);

        const totalHcf = this.getOriginalHcf(slot);

        const ratio = totalHcf / this.portfolioDefaultQty;

        return marketPrice * ratio;
    }

    getOriginalHcf(slot: 'first' | 'second' = 'first'): number {
        const orders = (isVoid(slot) || slot == 'first')
            ? this._config.orders
            : this._config.orders2;

        const qtties = orders.flatMap(og => {
            const qty = og.orders
                .map(y => y.qty);
            return qty;
        });

        const hcf = findHCF(qtties);

        return hcf;
    }

    // New Prices
    getNewMarketPrice(): number {
        const remainingOrders = this.orderGroups
            .flatMap(og => og.orders)
            .filter(x => {
                if (this.splitQty) {
                    return !x.isSettled;
                } else {
                    return !(x as any).moved;
                }
            });

        let remainingSum = 0;

        if (remainingOrders.length > 0) {
            const leftOverQtties = remainingOrders
                .map(x => x.leftOverQty);

            const hcf = findHCF(leftOverQtties);

            const prices = remainingOrders.map(x => {
                const price = Math.abs(x.price) * x.leftOverQty * -1;
                return price;
            })

            const sum = prices.reduce((p, c) => p + c, 0);

            remainingSum = sum / hcf;
        }

        const chunkPrices = this.dropZones.map(x => this.getChunkMarketPrice(x) || 0);

        const sumOfChunks = chunkPrices.reduce((p, c) => p + c, 0) || 0;

        const total = remainingSum + sumOfChunks;

        return total;
    }

    getNewLedgerPrice(slot: 'first'|'second' = 'first'): number {

        const orderGroups = (isVoid(slot) || slot == 'first')
            ? this.orderGroups
            : this.orderGroups2;

        const remainingOrders = orderGroups
            .flatMap(og => og.orders)
            .filter(x => {
                if (this.splitQty) {
                    return !x.isSettled;
                } else {
                    return !(x as any).moved;
                }
            });

        let remainingSum = 0;

        if (remainingOrders.length > 0) {
            const leftOverQtties = remainingOrders
                .map(x => x.leftOverQty);

            const hcf = findHCF(leftOverQtties);

            const prices = remainingOrders.map(x => {
                const price = Math.abs(x.price) * x.leftOverQty * -1;
                return price;
            });

            const ratio = hcf / this.portfolioDefaultQty;

            const sum = prices.reduce((p, c) => p + c, 0);

            remainingSum = (sum / hcf) * ratio;
        }

        const chunkPrices = this.dropZones.map(x => this.getChunkLedgerPrice(x) || 0);

        const sumOfChunks = chunkPrices.reduce((p, c) => p + c, 0) || 0;

        const total = remainingSum + sumOfChunks;

        return total;
    }

    // Chunks
    getChunkHcf(chunk: DropZone): number {
        const numbers = chunk.items.map(x => x.qty);
        const hcf = findHCF(numbers);
        return hcf;
    }

    getChunkLedgerPrice(zone: DropZone, realPrice?: boolean) {

        if (isVoid(zone.items)) {
            return undefined;
        }

        const mp = this.getChunkMarketPrice(zone, realPrice);

        const hcf = this.getChunkHcf(zone);

        const ratio = hcf / this.portfolioDefaultQty;

        return mp * ratio;
    }

    getChunkMarketPrice(zone: DropZone, realPrice?: boolean) {

        if (isVoid(zone.items)) {
            return undefined;
        }

        const totalHcf = this.getChunkHcf(zone);

        const prices = zone.items.map(x => {
            const px = Math.abs(x.price) * x.qty * -1;
            return px;
        });

        const total = prices.reduce((p, c) => p + c, 0);

        const adjusted = total / totalHcf;

        if (!realPrice) {
            if (isValidNumber(zone.limitPrice, true)) {
                return zone.limitPrice;
            }
        }
        return adjusted;
    }

    anyDropZoneHasLimitPrice() {
        return this.dropZones.some(x => isValidNumber(x.limitPrice, true));
    }

    copySingleOrderToDestination(destination: string, zone: DropZone) {
        let orders = zone.items;

        const cbArg = {
            orderToCopy: zone.title,
            orders,
            limitPrice: zone.limitPrice
        } as RegroupedOrders;

        this._config.copyCallback([cbArg], destination);
    }

    copyAllOrdersToDestination(destination: string) {
        const allOrders = this.dropZones.map(dz => {

            // const orders = dz.items.map(y => y.order);
            const orders = dz.items;

            return {
                orderToCopy: dz.title,
                orders,
                limitPrice: dz.limitPrice
            } as RegroupedOrders;
        });

        this._config.copyCallback(allOrders, destination);
    }

    canCopySingleOrder(zone: DropZone) {
        return zone.items.length > 0;
    }
}